import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatIDR, fmtDate } from "@/lib/constants";
import { X, ExternalLink, ShieldCheck, AlertTriangle, ArrowRight, Pencil } from "lucide-react";
import CompleteIdentityDialog from "@/components/collection/CompleteIdentityDialog";

const Row = ({ label, children, testid }) => (
  <div className="flex justify-between gap-3 py-0.5 text-xs border-b border-slate-50" data-testid={testid}>
    <span className="text-slate-500">{label}</span><span className="text-slate-800 text-right font-medium">{children}</span>
  </div>
);

export const ReasonBadges = ({ reasons }) => (
  <div className="flex flex-wrap gap-1">
    {(reasons || []).map((r) => (
      <span key={r.code} title={r.code} className={`px-1.5 py-0.5 text-[10px] border ${r.blocking ? "border-rose-300 bg-rose-50 text-rose-800" : r.code === "WAITING_FAT_REVIEW" ? "border-sky-300 bg-sky-50 text-sky-800" : "border-amber-300 bg-amber-50 text-amber-800"}`}>
        {r.label}
      </span>
    ))}
  </div>
);

const PreviewPanel = ({ pv, busy, onBack, onConfirm }) => (
  <div className="space-y-2" data-testid="draft-verify-preview">
    <div className="text-xs font-semibold text-slate-700">Pratinjau pengesahan — belum ada data yang disimpan.</div>
    <Row label="Status">{pv.state_before} <ArrowRight size={10} className="inline" /> <b className="text-emerald-700">{pv.state_after}</b></Row>
    <Row label="Nilai bruto resmi">{formatIDR(pv.gross_official)}</Row>
    <Row label="Kas yang seharusnya diterima">{formatIDR(pv.cash_due)}</Row>
    <Row label="Penerimaan resmi yang diakui">{formatIDR(pv.received_recognized)}</Row>
    <Row label="Outstanding resmi" testid="pv-outstanding">{formatIDR(pv.outstanding_official)}</Row>
    <Row label="Total Piutang resmi" testid="pv-ar-impact">{formatIDR(pv.ar_total_before)} <ArrowRight size={10} className="inline" /> {formatIDR(pv.ar_total_after)}</Row>
    <Row label="Status jatuh tempo / aging" testid="pv-due-state">{pv.due_state_label}{pv.enters_missing_due_queue ? " — masuk antrean Isi Due Date" : ""}</Row>
    <Row label="Catatan FAT">{pv.reason || "—"}</Row>
    {pv.blocking_reasons?.length > 0 && (
      <div className="border border-rose-300 bg-rose-50 p-2 text-[11px] text-rose-800" data-testid="pv-blocking">
        <AlertTriangle size={12} className="inline mr-1" /> Belum dapat diverifikasi: {pv.blocking_reasons.map((b) => b.label).join("; ")}
      </div>
    )}
    <div className="flex justify-end gap-2 pt-2">
      <button onClick={onBack} disabled={busy} className="px-3 py-1.5 border border-slate-300 text-xs" data-testid="pv-back">Kembali</button>
      <button onClick={onConfirm} disabled={busy || !pv.can_verify} className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white text-xs font-bold uppercase" data-testid="pv-confirm">
        {busy ? "Memproses…" : "KONFIRMASI VERIFIKASI INVOICE"}
      </button>
    </div>
  </div>
);

const ActionBar = ({ d, row, state, set, busy, onAction, onEdit }) => (
  <div className="space-y-2 border-t border-slate-200 pt-3">
    <textarea value={state.reason} onChange={(e) => set({ reason: e.target.value })} rows={2} placeholder="Catatan / alasan FAT (wajib untuk Minta Perbaikan, Tolak, dan pengecualian legacy)"
      className="w-full border border-slate-300 px-2 py-1 text-xs" data-testid="draft-reason" />
    {row.reasons.some((r) => r.code === "LEGACY_SOURCE_UNCONFIRMED") && (
      <label className="flex items-center gap-2 text-[11px]"><input type="checkbox" checked={state.confirm_legacy} onChange={(e) => set({ confirm_legacy: e.target.checked })} data-testid="draft-confirm-legacy" />
        Saya telah memeriksa sumber penerimaan legacy dan mengonfirmasinya (alasan wajib).</label>
    )}
    {row.reasons.some((r) => r.code === "MISSING_INVOICE_NUMBER") && (
      <label className="flex items-center gap-2 text-[11px]"><input type="checkbox" checked={state.legacy_number_exception} onChange={(e) => set({ legacy_number_exception: e.target.checked })} data-testid="draft-legacy-number" />
        Pengecualian legacy: invoice ini memang tidak memiliki nomor resmi (tidak membuat nomor palsu, alasan wajib).</label>
    )}
    <div className="flex flex-wrap gap-2 justify-end">
      <button onClick={onEdit} disabled={!d.can_complete} className="px-3 py-1.5 border border-slate-300 text-xs inline-flex items-center gap-1 disabled:opacity-40" data-testid="draft-btn-complete"><Pencil size={11} /> Lengkapi Data / Simpan Draft</button>
      <button onClick={() => onAction("request-fix")} disabled={busy || !d.can_review} className="px-3 py-1.5 border border-amber-400 text-amber-800 text-xs disabled:opacity-40" data-testid="draft-btn-fix">Minta Perbaikan</button>
      <button onClick={() => onAction("reject")} disabled={busy || !d.can_review} className="px-3 py-1.5 border border-rose-400 text-rose-700 text-xs disabled:opacity-40" data-testid="draft-btn-reject">Tolak / Batalkan</button>
      <button onClick={() => onAction("preview")} disabled={busy || !d.can_review} className="px-3 py-1.5 bg-[#0B1D3A] text-white text-xs inline-flex items-center gap-1 disabled:opacity-40" data-testid="draft-btn-review"><ShieldCheck size={12} /> Tinjau & Verifikasi</button>
    </div>
  </div>
);

/** Dialog pemeriksaan FAT per invoice draft (tanpa verifikasi massal). */
export default function DraftReviewDialog({ open, target, canComplete, onClose, onChanged }) {
  const [d, setD] = useState(null);
  const [pv, setPv] = useState(null);
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState(false);
  const [st, setSt] = useState({ reason: "", confirm_legacy: false, legacy_number_exception: false });
  const set = (p) => setSt((s) => ({ ...s, ...p }));

  const load = useCallback(async () => {
    if (!target) return;
    try {
      const { data } = await api.get(`/collection/draft-queue/${target.project_id}/${target.entry_id}`);
      setD({ ...data, can_complete: canComplete });
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal memuat invoice."); }
  }, [target, canComplete]);

  useEffect(() => {
    if (open) { setD(null); setPv(null); setSt({ reason: "", confirm_legacy: false, legacy_number_exception: false }); load(); }
  }, [open, load]);

  if (!open || !target) return null;
  const row = d?.row;
  const base = `/collection/draft-queue/${target.project_id}/${target.entry_id}`;
  const body = () => ({ expected_version: row?.version, reason: st.reason || null, confirm_legacy: st.confirm_legacy, legacy_number_exception: st.legacy_number_exception });

  const act = async (kind) => {
    if ((kind === "request-fix" || kind === "reject") && !st.reason.trim()) { toast.error("Alasan wajib diisi."); return; }
    setBusy(true);
    try {
      if (kind === "preview") { const { data } = await api.post(`${base}/preview`, body()); setPv(data); return; }
      const { data } = await api.post(`${base}/${kind}`, body());
      const msg = { verify: "Invoice terverifikasi — masuk piutang resmi.", "request-fix": "Permintaan perbaikan tersimpan.", reject: "Invoice ditolak & dikeluarkan dari antrean." }[kind];
      toast.success(data.unchanged ? "Tidak ada perubahan (sudah diproses)." : msg);
      if (kind === "verify" && data.enters_missing_due_queue) toast.info("Invoice resmi ini belum punya jatuh tempo — kini muncul di antrean Isi Due Date.");
      onChanged?.(); onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aksi gagal.");
      if (e?.response?.status === 409) load();
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="draft-review-dialog">
      <div className="bg-white border border-slate-300 max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2 bg-amber-50">
          <div className="flex-1">
            <div className="font-bold text-sm text-[#0B1D3A] uppercase">Pemeriksaan FAT — {row?.invoice_label || "Memuat…"}</div>
            <div className="text-[11px] text-slate-600">{row?.client} · {row?.project_label}{row?.wo_number ? ` · WO ${row.wo_number}` : ""}{row?.variation_order_name ? ` · VO ${row.variation_order_name}` : ""}</div>
          </div>
          <button onClick={onClose} disabled={busy} data-testid="draft-close"><X size={18} className="text-slate-400" /></button>
        </div>
        <div className="flex-1 overflow-auto p-4 space-y-3">
          {!d ? <div className="text-sm text-slate-400">Memuat…</div> : !row ? (
            <div className="text-sm text-slate-600" data-testid="draft-not-in-queue">Invoice ini sudah tidak berada di antrean draft (status: {d.review_status}).</div>
          ) : pv ? (
            <PreviewPanel pv={pv} busy={busy} onBack={() => setPv(null)} onConfirm={() => act("verify")} />
          ) : (
            <>
              <ReasonBadges reasons={row.reasons} />
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Row label="Nomor invoice">{row.invoice_label}</Row>
                  <Row label="Referensi eksternal">{row.external_reference || "—"}</Row>
                  <Row label="Transaksi sumber">
                    {row.source_transaction?.link ? <a href={row.source_transaction.link} target="_blank" rel="noreferrer" className="text-sky-700 inline-flex items-center gap-1" data-testid="draft-open-source"><ExternalLink size={11} /> Buka Transaksi Sumber</a> : "—"}
                  </Row>
                  <Row label="Tanggal invoice">{row.invoice_date ? fmtDate(row.invoice_date) : "Belum diisi"}</Row>
                  <Row label="Jatuh tempo">{row.due_date ? fmtDate(row.due_date) : "Belum diisi"}</Row>
                  <Row label="PIC">{row.pic_collection_name || "—"}</Row>
                  <Row label="Status verifikasi">{row.verification_state_label}</Row>
                  <Row label="Status pembayaran">{row.payment_state_label}</Row>
                </div>
                <div>
                  <Row label="Nilai bruto">{formatIDR(row.gross_amount)}</Row>
                  <Row label="PPh">{formatIDR(row.tax_amount)}</Row>
                  <Row label="Retensi">{formatIDR(row.retention_amount)}</Row>
                  <Row label="Kas yang seharusnya diterima">{formatIDR(row.cash_due)}</Row>
                  <Row label="Penerimaan tertaut">{formatIDR(row.received_linked)}</Row>
                  <Row label="Sisa (informatif)">{formatIDR(row.remaining_informative)}</Row>
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase font-semibold text-slate-500 mb-1">Receipt tertaut ({d.receipts.length})</div>
                {d.receipts.length === 0 ? <div className="text-xs text-slate-400">Belum ada receipt.</div> : d.receipts.map((r, i) => (
                  <div key={r.id || i} className="text-xs flex justify-between border-b border-slate-50"><span>{fmtDate(r.date || r.receipt_date)} · {r.reference || r.note || "—"}</span><span>{formatIDR(r.amount)}</span></div>
                ))}
              </div>
              <div>
                <div className="text-[10px] uppercase font-semibold text-slate-500 mb-1">Riwayat perubahan & pemeriksaan</div>
                <div className="max-h-32 overflow-auto" data-testid="draft-history">
                  {d.history.length === 0 ? <div className="text-xs text-slate-400">Belum ada riwayat.</div> : d.history.map((h) => (
                    <div key={h.id} className="text-[11px] text-slate-600 border-b border-slate-50">{fmtDate(h.created_at)} · {h.actor_name} · {h.field}: {String(h.old_value ?? "—")} → {String(h.new_value ?? "—")}{h.reason ? ` (${h.reason})` : ""}</div>
                  ))}
                </div>
              </div>
              <ActionBar d={d} row={row} state={st} set={set} busy={busy} onAction={act} onEdit={() => setEdit(true)} />
            </>
          )}
        </div>
      </div>
      <CompleteIdentityDialog open={edit} row={row} onClose={() => setEdit(false)} onSaved={() => { setEdit(false); load(); onChanged?.(); }} />
    </div>
  );
}
